import React, { useEffect, useState } from 'react';
import { useOptionalSidebar } from '../ui/sidebar';
import { X, Users, Search } from 'lucide-react';
import { fetchUserAttributes } from '@aws-amplify/auth';
import { useStreamChat } from '../../context/StreamChatContext';
import { getUserSettings, getAllUsers } from '../../utils/api';
import { Chat, LoadingIndicator } from 'stream-chat-react';
import styles from './Chat.module.css';

import CustomChannelList from './ChannelList/CustomChannelList';
import CustomChannelHeader from './Header/CustomChannelHeader';
import CustomMessageList from './Messages/CustomMessageList';
import CustomMessageInput from './Input/CustomMessageInput';
import { createStreamChatShareModal } from './utils/filterShareUtils';
import { createAIChatShareModal } from './utils/aiChatShareUtils';

const StreamChatBox = ({ isOpen, onClose, userId, isSidebarCollapsed = true }) => {
  // Clear the desktop sidebar: inset shell (16rem open / 3rem icon rail +
  // gutters) via context; classic shell via the prop its layout passes.
  const sidebar = useOptionalSidebar();
  const sidebarOffset = sidebar
    ? (sidebar.state === 'collapsed' ? '5rem' : '17rem')
    : (isSidebarCollapsed ? '6rem' : '17rem');
  const { chatClient, loading, error, initializeChat, user, fetchUnreadCount } = useStreamChat();
  const [isAnimating, setIsAnimating] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [isMobileChannelView, setIsMobileChannelView] = useState(false);

  // New channel creation state
  const [showCreateChannelModal, setShowCreateChannelModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const isModal = onClose !== undefined;
  const isMobile = window.innerWidth <= 768;

  // Control animation timing for modal mode
  useEffect(() => {
    if (!isModal) return;

    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      setTimeout(() => {
        setIsAnimating(false);
        document.body.style.overflow = '';
      }, 300);
    }
  }, [isOpen, isModal]);

  // Fetch user email immediately when component mounts or opens
  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const attributes = await fetchUserAttributes();
        const userEmailFromCognito = attributes?.email;

        if (!userEmailFromCognito) {
          console.error('[StreamChatBox] No email found in user attributes');
          return;
        }

        setUserEmail(userEmailFromCognito);
      } catch (error) {
        console.error('[StreamChatBox] Error fetching user email:', error);
      }
    };

    if (isOpen && !userEmail) {
      fetchUserEmail();
    }
  }, [isOpen, userEmail]);

  // Separate effect for initializing chat when we have user email
  useEffect(() => {
    const initializeChatWithUserData = async () => {
      try {
        const userSettings = await getUserSettings(userEmail);

        if (userSettings) {
          setOrganizationId(userSettings.organizationId);
          await initializeChat(userEmail, userSettings);
        }
      } catch (error) {
        console.error('[StreamChatBox] Error initializing chat:', error);
      }
    };

    if (isOpen && userEmail && !chatClient && !loading) {
      initializeChatWithUserData();
    }
  }, [isOpen, userEmail, chatClient, loading, initializeChat]);

  const handleChannelSelect = async (channel) => {
    setCurrentChannel(channel);
    if (isMobile) {
      setIsMobileChannelView(true);
    }

    try {
      await channel.markRead();
      setTimeout(() => {
        if (fetchUnreadCount) {
          fetchUnreadCount();
        }
      }, 100);
    } catch (error) {
      console.error('[StreamChatBox] Error marking channel as read:', error);
    }
  };

  const handleBack = () => {
    setIsMobileChannelView(false);
    setCurrentChannel(null);
  };

  const handleChannelDeleted = () => {
    setCurrentChannel(null);
    if (isMobile) {
      setIsMobileChannelView(false);
    }
  };

  // Fetch ALL users from the organization (not just active chat users)
  const fetchUsersFromOrganization = async () => {
    try {
      const currentOrgCode = localStorage.getItem('currentOrgCode');

      if (!currentOrgCode) {
        console.error('No current organization code found in localStorage');
        return [];
      }

      const allUsers = await getAllUsers();

      const orgUsers = allUsers.filter(u => {
        const hasOrgAccess = u.orgAccess && u.orgAccess[currentOrgCode];
        const isNotCurrentUser = u.email !== userEmail;
        const isNotDeleted = !u.isDeleted;

        return hasOrgAccess && isNotCurrentUser && isNotDeleted;
      });

      return orgUsers.map(u => ({
        id: u.email.replace(/[^a-zA-Z0-9@_-]/g, '_').toLowerCase(),
        email: u.email,
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
        image: u.profilePictureThumb || u.profilePicture
      }));
    } catch (error) {
      console.error('Error fetching organization users:', error);
      return [];
    }
  };

  const handleCreateChannel = async () => {
    const currentOrgCode = localStorage.getItem('currentOrgCode');

    if (!userEmail || !currentOrgCode) {
      console.error('[StreamChatBox] User data not loaded yet.', { userEmail, currentOrgCode });
      if (!userEmail) {
        try {
          const attributes = await fetchUserAttributes();
          const email = attributes?.email;
          if (email) {
            setUserEmail(email);
            setTimeout(() => handleCreateChannel(), 100);
            return;
          }
        } catch (error) {
          console.error('[StreamChatBox] Failed to fetch user email:', error);
        }
      }
      return;
    }

    setShowCreateChannelModal(true);
    setUserSearchQuery('');

    try {
      setLoadingUsers(true);
      const users = await fetchUsersFromOrganization();
      setAvailableUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.find(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const createNewChannel = async () => {
    if (!chatClient || selectedUsers.length === 0) return;

    setCreatingChannel(true);
    try {
      const memberIds = [user.id, ...selectedUsers.map(u => u.id)];

      const channel = chatClient.channel('messaging', {
        members: memberIds,
        name: selectedUsers.length === 1
          ? `Chat with ${selectedUsers[0].name}`
          : `Group chat with ${selectedUsers.map(u => u.name).join(', ')}`,
      });

      await channel.create();
      await channel.watch();

      setShowCreateChannelModal(false);
      setSelectedUsers([]);
      setCurrentChannel(channel);

      if (isMobile) {
        setIsMobileChannelView(true);
      }
    } catch (error) {
      console.error('Error creating channel:', error);
    } finally {
      setCreatingChannel(false);
    }
  };

  // Filter users by search query
  const filteredUsers = userSearchQuery
    ? availableUsers.filter(u =>
        u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(userSearchQuery.toLowerCase())
      )
    : availableUsers;

  // Send filter share message function
  const sendFilterShareMessage = async (filterData) => {
    if (!chatClient) {
      console.error('No chat client available for sending filter share message');
      return;
    }

    try {
      const filter = { members: { $in: [user.id] } };
      const sort = { last_message_at: -1 };
      const channels = await chatClient.queryChannels(filter, sort, { limit: 20 });

      const orgUsers = await fetchUsersFromOrganization();

      await createStreamChatShareModal(filterData, channels, user, chatClient, orgUsers);
    } catch (error) {
      console.error('Error in sendFilterShareMessage:', error);
      throw error;
    }
  };

  // Make sendFilterShareMessage available globally for PowerBI integration
  useEffect(() => {
    if (chatClient && user) {
      window.sendFilterShareMessage = sendFilterShareMessage;
    }
    return () => {
      window.sendFilterShareMessage = null;
    };
  }, [chatClient, user]);

  // Native report view share: the URL carries the complete report state
  // (filters, drill-down expansion, scroll position), so the attachment is
  // just the link + display labels. Reuses the same channel-selection modal.
  const sendNativeReportShareMessage = async ({ url, reportCategory, reportTitle, subtitle }) => {
    if (!chatClient) {
      console.error('No chat client available for sending native report share message');
      throw new Error('Chat unavailable');
    }
    const filter = { members: { $in: [user.id] } };
    const sort = { last_message_at: -1 };
    const channels = await chatClient.queryChannels(filter, sort, { limit: 20 });
    const orgUsers = await fetchUsersFromOrganization();
    await createStreamChatShareModal(
      {
        attachmentType: 'native_report_share',
        url,
        reportCategory,
        reportTitle,
        subtitle,
        reportName: reportCategory ? `${reportCategory} · ${reportTitle}` : reportTitle,
        timestamp: new Date().toISOString(),
      },
      channels,
      user,
      chatClient,
      orgUsers
    );
  };

  useEffect(() => {
    if (chatClient && user) {
      window.sendNativeReportShareMessage = sendNativeReportShareMessage;
    }
    return () => {
      window.sendNativeReportShareMessage = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatClient, user]);

  // Send AI chat session share message. Mirrors sendFilterShareMessage but
  // opens the AI-specific share modal, which forks the session server-side
  // to each recipient before posting a Stream Chat notification.
  const sendAIChatShareMessage = async (sessionInfo) => {
    if (!chatClient) {
      console.error('No chat client available for sending AI chat share message');
      return;
    }
    try {
      const filter = { members: { $in: [user.id] } };
      const sort = { last_message_at: -1 };
      const channels = await chatClient.queryChannels(filter, sort, { limit: 20 });
      const orgUsers = await fetchUsersFromOrganization();
      await createAIChatShareModal(sessionInfo, channels, user, chatClient, orgUsers);
    } catch (error) {
      console.error('Error in sendAIChatShareMessage:', error);
      throw error;
    }
  };

  // Make sendAIChatShareMessage available globally so AIChat.js can open the
  // share modal without importing the StreamChat context directly.
  useEffect(() => {
    if (chatClient && user) {
      window.sendAIChatShareMessage = sendAIChatShareMessage;
    }
    return () => {
      window.sendAIChatShareMessage = null;
    };
  }, [chatClient, user]);

  // Listen for channel selection events from filter sharing
  useEffect(() => {
    const handleSelectChatChannel = (event) => {
      const { channel } = event.detail;
      handleChannelSelect(channel);
    };

    const checkForStoredSelectedChannel = () => {
      try {
        const storedChannel = sessionStorage.getItem('selectedChatChannel');
        if (storedChannel && chatClient) {
          const { id, timestamp } = JSON.parse(storedChannel);

          if (Date.now() - timestamp < 30000) {
            sessionStorage.removeItem('selectedChatChannel');

            setTimeout(async () => {
              try {
                const channel = chatClient.channel('messaging', id);
                await channel.watch();
                handleChannelSelect(channel);
              } catch (error) {
                console.error('[StreamChatBox] Error selecting stored channel:', error);
              }
            }, 500);
          } else {
            sessionStorage.removeItem('selectedChatChannel');
          }
        }
      } catch (error) {
        console.error('[StreamChatBox] Error checking for stored selected channel:', error);
      }
    };

    window.addEventListener('selectChatChannel', handleSelectChatChannel);

    if (chatClient && isOpen) {
      checkForStoredSelectedChannel();
    }

    return () => {
      window.removeEventListener('selectChatChannel', handleSelectChatChannel);
    };
  }, [chatClient, isOpen]);

  if (isModal && !isOpen && !isAnimating) return null;

  // Loading state
  if (loading) {
    return (
      <div className={isModal ? styles.modalBackdrop : 'h-full flex items-center justify-center'}>
        <div className={styles.modalCard} style={{ maxWidth: 320, padding: 'var(--sp-8)', textAlign: 'center' }}>
          <div className={styles.spinner} style={{ margin: '0 auto var(--sp-4)' }} />
          <p style={{ color: 'var(--lodge-onyx-500)', fontSize: 14 }}>Connecting to chat...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={isModal ? styles.modalBackdrop : 'h-full flex items-center justify-center'}>
        <div className={styles.modalCard} style={{ maxWidth: 400, padding: 'var(--sp-8)' }}>
          <div style={{ color: 'var(--color-error)', marginBottom: 'var(--sp-4)' }}>
            <X size={24} />
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--lodge-onyx-800)', marginBottom: 'var(--sp-2)' }}>
            Connection Error
          </h3>
          <p style={{ fontSize: 14, color: 'var(--lodge-onyx-500)', marginBottom: 'var(--sp-4)' }}>{error.message}</p>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <button
              onClick={() => initializeChat(userEmail, { email: userEmail })}
              className={styles.btnPrimary}
            >
              Try Again
            </button>
            {isModal && (
              <button onClick={onClose} className={styles.btnGhost}>
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Not connected state
  if (!chatClient || !user) {
    return (
      <div className={isModal ? styles.modalBackdrop : 'h-full flex items-center justify-center'}>
        <div className={styles.modalCard} style={{ maxWidth: 320, padding: 'var(--sp-8)', textAlign: 'center' }}>
          <p style={{ color: 'var(--lodge-onyx-500)', fontSize: 14 }}>Chat not connected</p>
          {isModal && (
            <button onClick={onClose} className={styles.btnGhost} style={{ marginTop: 'var(--sp-4)' }}>
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  const chatContent = (
    <Chat client={chatClient}>
      <div className={styles.chatRoot}>
        {/* Channel List */}
        <div style={{ display: isMobile && isMobileChannelView ? 'none' : 'flex', width: isMobile ? '100%' : undefined, flexShrink: 0 }}>
          <CustomChannelList
            organizationId={organizationId}
            onChannelSelect={handleChannelSelect}
            onCreateChannel={handleCreateChannel}
            isMobile={isMobile}
          />
        </div>

        {/* Chat Area */}
        {currentChannel && (
          <div className={styles.chatArea} style={{ display: isMobile && !isMobileChannelView ? 'none' : undefined }}>
            <CustomChannelHeader
              onBack={handleBack}
              isMobile={isMobile}
              channel={currentChannel}
              onDeleteChannel={handleChannelDeleted}
            />
            <CustomMessageList channel={currentChannel} />
            <CustomMessageInput channel={currentChannel} />
          </div>
        )}

        {/* Empty state when no channel selected on desktop */}
        {!currentChannel && !isMobile && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--lodge-onyx-50)', minWidth: 0 }}>
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <Users size={32} />
              </div>
              <h3 className={styles.emptyTitle}>Welcome to Team Chat</h3>
              <p className={styles.emptyText}>
                Select a conversation from the sidebar to start messaging, or create a new conversation to chat with your team.
              </p>
              <button onClick={handleCreateChannel} className={styles.btnPrimary} style={{ marginTop: 'var(--sp-6)' }}>
                Start New Conversation
              </button>
            </div>
          </div>
        )}
      </div>
    </Chat>
  );

  // Modal mode
  if (isModal) {
    return (
      <div className={styles.modalShell} style={{ opacity: isOpen ? 1 : 0 }}>
        <div className={styles.modalShellBackdrop} onClick={onClose} />
        <div
          className={styles.modalShellCard}
          style={{
            transform: isOpen ? 'scale(1)' : 'scale(0.97)',
            left: isMobile ? '1rem' : sidebarOffset,
          }}
        >
          {/* Header */}
          <div className={styles.modalShellHeader}>
            <h2 className={styles.modalShellTitle}>Messages</h2>
            <button onClick={onClose} className={styles.modalClose}>
              <X size={20} />
            </button>
          </div>

          {/* Chat Content */}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {chatContent}
          </div>
        </div>

        {/* Create Channel Modal */}
        {showCreateChannelModal && (
          <div className={styles.modalBackdrop} style={{ zIndex: 'calc(var(--z-modal) + 10)' }}>
            <div className={styles.modalCard}>
              <div className={styles.modalHeader}>
                <div>
                  <h3 className={styles.modalTitle}>Start New Conversation</h3>
                  <p className={styles.modalSubtitle}>Choose team members to chat with</p>
                </div>
                <button
                  onClick={() => { setShowCreateChannelModal(false); setSelectedUsers([]); }}
                  className={styles.modalClose}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Search users */}
              <div style={{ padding: 'var(--sp-3) var(--sp-6) 0' }}>
                <div className={styles.searchWrap}>
                  <Search size={16} className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search team members..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className={styles.searchInput}
                  />
                </div>
              </div>

              <div className={styles.modalBody}>
                {loadingUsers ? (
                  <div className={styles.emptyState} style={{ padding: 'var(--sp-8) 0' }}>
                    <div className={styles.spinner} />
                    <p style={{ marginTop: 'var(--sp-4)', color: 'var(--lodge-onyx-400)', fontSize: 13 }}>
                      Loading team members...
                    </p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className={styles.emptyState} style={{ padding: 'var(--sp-8) 0' }}>
                    <div className={styles.emptyIcon}>
                      <Users size={28} />
                    </div>
                    <h4 className={styles.emptyTitle}>
                      {userSearchQuery ? 'No results found' : 'No team members found'}
                    </h4>
                    <p className={styles.emptyText}>
                      {userSearchQuery
                        ? 'Try a different search term'
                        : 'No other users found in your organization.'}
                    </p>
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--lodge-onyx-500)', marginBottom: 'var(--sp-3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {filteredUsers.length} team member{filteredUsers.length !== 1 ? 's' : ''}
                    </p>
                    {filteredUsers.map((u) => {
                      const isSelected = selectedUsers.find(s => s.id === u.id);
                      return (
                        <div
                          key={u.id}
                          className={`${styles.userItem} ${isSelected ? styles.selected : ''}`}
                          onClick={() => toggleUserSelection(u)}
                        >
                          <div style={{ position: 'relative', flexShrink: 0, marginRight: 'var(--sp-3)' }}>
                            <div className={styles.userItemAvatar}>
                              {u.image ? (
                                <img src={u.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                              ) : (
                                u.name.charAt(0).toUpperCase()
                              )}
                            </div>
                            {isSelected && (
                              <div className={styles.userItemCheck}>
                                <svg width="10" height="10" viewBox="0 0 20 20" fill="white">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p className={styles.userItemName}>{u.name}</p>
                            <p className={styles.userItemEmail}>{u.email}</p>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              <div className={styles.modalFooter}>
                <div>
                  {selectedUsers.length > 0 && (
                    <div className={styles.selectionCount}>
                      <span className={styles.selectionBadge}>{selectedUsers.length}</span>
                      <span style={{ fontWeight: 500 }}>
                        {selectedUsers.length} member{selectedUsers.length !== 1 ? 's' : ''} selected
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
                  <button
                    onClick={() => { setShowCreateChannelModal(false); setSelectedUsers([]); }}
                    className={styles.btnGhost}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createNewChannel}
                    disabled={selectedUsers.length === 0 || creatingChannel}
                    className={styles.btnPrimary}
                  >
                    {creatingChannel ? (
                      <>
                        <div className={styles.spinner} style={{ width: 18, height: 18, borderWidth: 2 }} />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Users size={16} />
                        Start Chat
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Embedded mode
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {chatContent}
    </div>
  );
};

export default StreamChatBox;
